import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { productService } from '../services/productService';
import { InvoiceModal } from '../components/products/InvoiceModal';
import { UserRole } from '../types';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { InvoiceDetailModal } from '../components/products/InvoiceDetailModal';
import { PreOrderDeliveryWarningModal } from '../components/products/PreOrderDeliveryWarningModal';

interface InvoiceStat {
    id: string;
    date: string | null;
    receivedDate: string | null;
    totalItems: number;
    inTransitItems: number;
    status: 'in_transit' | 'received';
}

export const PurchasesPage: React.FC = () => {
    const { user } = useAuth();
    const isAdmin = user?.rol_usr === UserRole.ADMIN;
    const [invoices, setInvoices] = useState<InvoiceStat[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'in_transit' | 'received'>('all');
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [showRecent, setShowRecent] = useState(false);
    const [showFilters, setShowFilters] = useState(false); // New state for toggling filters

    // Confirmation State
    const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; invoiceId: string | null }>({
        isOpen: false,
        invoiceId: null
    });

    // Delivery Notification State
    const [deliveryPreOrders, setDeliveryPreOrders] = useState<any[]>([]);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadInvoices(searchTerm, statusFilter);
        }, 400); // 400ms debounce
        return () => clearTimeout(timer);
    }, [searchTerm, statusFilter]);

    const loadInvoices = async (search?: string, status?: string) => {
        setLoading(true);
        try {
            const params: any = {};
            if (search) params.search = search;
            if (status && status !== 'all') params.status = status;

            const response = await productService.getInvoices(params);
            if (response.success) {
                setInvoices(response.data);
            }
        } catch (error) {
            console.error('Error loading invoices:', error);
            toast.error('Error al cargar historial de invoices');
        } finally {
            setLoading(false);
        }
    };

    const filteredInvoices = React.useMemo(() => {
        let filtered = [...invoices];

        // Search and Status are now handled primarily by the backend.
        // We keep other local filters (Date Range, Recent) below.

        // 3. Date Range
        if (dateStart) {
            const start = new Date(dateStart);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(inv => {
                const date = inv.date ? new Date(inv.date) : null;
                return date ? date >= start : false;
            });
        }
        if (dateEnd) {
            const end = new Date(dateEnd);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(inv => {
                const date = inv.date ? new Date(inv.date) : null;
                return date ? date <= end : false;
            });
        }

        // 4. Recent (Last 3 days for example, or typical "Recent" concept)
        if (showRecent) {
            const threeDaysAgo = new Date();
            threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
            filtered = filtered.filter(inv => {
                const date = inv.date ? new Date(inv.date) : null;
                return date ? date >= threeDaysAgo : false;
            });
        }

        return filtered;
    }, [invoices, searchTerm, statusFilter, dateStart, dateEnd, showRecent]);

    const handleActivateClick = (invoiceId: string) => {
        setConfirmModal({ isOpen: true, invoiceId });
    };

    const confirmActivate = async () => {
        const invoiceId = confirmModal.invoiceId;
        if (!invoiceId) return;

        setProcessingId(invoiceId);
        try {
            const response = await productService.activateInvoice(invoiceId);
            if (response.success) {
                toast.success('Invoice activada y recibida correctamente');
                loadInvoices();

                // Show WhatsApp notification modal if there are pre-orders in this invoice
                if (response.preOrderDetails && response.preOrderDetails.length > 0) {
                    setConfirmModal({ isOpen: false, invoiceId: null });
                    setDeliveryPreOrders(response.preOrderDetails);
                }
            } else {
                toast.error('Error al activar invoice: ' + response.message);
            }
        } catch (error) {
            console.error('Error activating invoice:', error);
            toast.error('Error al conectar con el servidor');
        } finally {
            setProcessingId(null);
        }
    };

    const handleViewDetails = (invoiceId: string) => {
        setViewInvoiceId(invoiceId);
    };

    const handleViewInventory = (invoiceId: string) => {

        navigator.clipboard.writeText(invoiceId);
        toast.success('ID copiado al portapapeles');
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Compras (Invoices)</h1>
                        <p className="text-gray-500 mt-1">Gestión de lotes de entrada de mercancía.</p>
                    </div>
                    {isAdmin && (
                        <button
                            onClick={() => setShowModal(true)}
                            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium flex items-center gap-2 shadow-lg hover:shadow-indigo-200"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            Nueva Invoice
                        </button>
                    )}
                </div>

                {/* Search and Filters */}
                <div className="bg-white rounded-lg shadow p-4 space-y-4">
                    <div className="flex items-center space-x-4">
                        <div className="flex-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Buscar por ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                            />
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${showFilters
                                ? 'bg-blue-50 border-blue-500 text-blue-700'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
                            </svg>
                            <span>Filtros</span>
                        </button>
                    </div>

                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as any)}
                                    className="w-full h-[42px] px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 shadow-sm"
                                >
                                    <option value="all">Todos los estados</option>
                                    <option value="in_transit">En Tránsito</option>
                                    <option value="received">Recibido</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                                <input
                                    type="date"
                                    value={dateStart}
                                    onChange={(e) => setDateStart(e.target.value)}
                                    className="w-full h-[42px] px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 shadow-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                                <input
                                    type="date"
                                    value={dateEnd}
                                    onChange={(e) => setDateEnd(e.target.value)}
                                    className="w-full h-[42px] px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 shadow-sm"
                                />
                            </div>

                            <div className="md:col-span-4 flex justify-between items-center bg-gray-50 p-3 rounded-lg mt-2">
                                <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={showRecent}
                                        onChange={(e) => setShowRecent(e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                                    />
                                    <span>Mostrar Recientes (3 días)</span>
                                </label>

                                {(searchTerm || statusFilter !== 'all' || dateStart || dateEnd || showRecent) && (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setStatusFilter('all');
                                            setDateStart('');
                                            setDateEnd('');
                                            setShowRecent(false);
                                        }}
                                        className="text-sm text-red-600 hover:text-red-800 font-medium px-2 py-1 hover:bg-red-50 rounded transition-colors"
                                    >
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Invoices List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-800">Historial de Invoices</h3>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">{filteredInvoices.length} Total</span>
                    </div>

                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Cargando invoices...</div>
                    ) : filteredInvoices.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 mb-3 text-gray-300">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            <p>No hay invoices que coincidan con los filtros.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {filteredInvoices.map((inv) => (
                                <div key={inv.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors group">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm ${inv.status === 'in_transit' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                                            }`}>
                                            {inv.status === 'in_transit' ? 'TR' : 'OK'}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h4 className="text-sm font-bold text-gray-900">{inv.id}</h4>
                                                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border ${inv.status === 'in_transit'
                                                    ? 'bg-amber-50 text-amber-600 border-amber-200'
                                                    : 'bg-green-50 text-green-600 border-green-200'
                                                    }`}>
                                                    {inv.status === 'in_transit' ? 'En Tránsito' : 'Recibido'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                                                        <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4h.25V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clipRule="evenodd" />
                                                    </svg>
                                                    {inv.status === 'received' && inv.receivedDate
                                                        ? `Recibido: ${new Date(inv.receivedDate).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                                                        : (inv.date ? `Creado: ${new Date(inv.date).toLocaleDateString('es-MX')}` : 'Fecha desc.')}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                                                        <path d="M7 8a3 3 0 100-6 3 3 0 000 6zM14.5 9a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM1.615 16.428a1.224 1.224 0 01-.569-1.175 6.002 6.002 0 0111.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 017 18a9.953 9.953 0 01-5.385-1.572zM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 00-1.588-3.755 4.502 4.502 0 015.874 2.636.818.818 0 01-.36.98A7.465 7.465 0 0114.5 16z" />
                                                    </svg>
                                                    {inv.totalItems} ítems
                                                </span>
                                                {inv.inTransitItems > 0 && (
                                                    <span className="text-amber-600 font-medium">
                                                        ({inv.inTransitItems} pendientes)
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => handleViewDetails(inv.id)}
                                            className="text-gray-400 hover:text-indigo-600 transition-colors p-2 rounded-full hover:bg-gray-100"
                                            title="Ver Detalle"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => handleViewInventory(inv.id)}

                                            className="text-gray-400 hover:text-indigo-600 transition-colors p-2 rounded-full hover:bg-gray-100"
                                            title="Copiar ID para buscar"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                                            </svg>
                                        </button>

                                        {isAdmin && inv.status === 'in_transit' && (
                                            <button
                                                onClick={() => {
                                                    if (inv.status === 'in_transit') {
                                                        handleActivateClick(inv.id);
                                                    }
                                                }}
                                                disabled={processingId === inv.id}
                                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1 ${
                                                    processingId === inv.id
                                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                        : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow'
                                                }`}
                                            >
                                                {processingId === inv.id ? (
                                                    <span className="animate-pulse">Procesando...</span>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                                                        </svg>
                                                        Recibir
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <InvoiceModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onSuccess={() => {
                    loadInvoices();
                    toast.success('Invoice creada. Procede a recibirla cuando llegue la mercancía.');
                }}
            />

            {(() => {
                const selectedInvoice = invoices.find(i => i.id === viewInvoiceId);
                return (
                    <InvoiceDetailModal
                        isOpen={!!viewInvoiceId}
                        onClose={() => setViewInvoiceId(null)}
                        invoiceId={viewInvoiceId}
                        date={selectedInvoice?.date}
                        receivedDate={selectedInvoice?.receivedDate}
                        onUpdate={() => loadInvoices()}
                    />
                );
            })()}

            <ConfirmationModal

                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                onConfirm={confirmActivate}
                title="¿Recibir Mercancía?"
                message={
                    <div>
                        <p>¿Estás seguro de que deseas procesar la recepción del Invoice <strong>{confirmModal.invoiceId}</strong>?</p>
                        <p className="mt-2 text-sm text-gray-500">Esto marcará los productos como <strong>ACTIVOS</strong> para venta general, <span className="text-amber-600 font-medium">excepto los Pre-orders</span>, que permanecerán reservados.</p>
                    </div>
                }
                confirmText="Sí, recibir existencias"
                confirmButtonColor="green"
            />

            <PreOrderDeliveryWarningModal
                isOpen={deliveryPreOrders.length > 0}
                onClose={() => setDeliveryPreOrders([])}
                preOrders={deliveryPreOrders}
            />
        </Layout>
    );
};

export default PurchasesPage;
